import React, { useEffect } from 'react';
import logo from '../../assets/images/amaze-logo-blue.png';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

const Hero = () => {
  const { isDarkMode } = useTheme();
  
  const handleLearnMore = () => {
    window.location.href = '/products';
  };

  // Add CSS animations to document head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes gradientWave {
        0%, 100% {
          background-position: 0% 50%;
        }
        50% {
          background-position: 100% 50%;
        }
      }

      @keyframes gradientFlow {
        0%, 100% {
          background-position: 0% 0%;
        }
        50% {
          background-position: 100% 100%;
        }
      }

      @keyframes breathe {
        0%, 100% {
          opacity: 0.15;
          transform: scale(1);
        }
        50% {
          opacity: 0.25;
          transform: scale(1.05);
        }
      }

      @keyframes float {
        0%, 100% {
          transform: translateY(0) translateX(0);
        }
        50% {
          transform: translateY(-10px) translateX(5px);
        }
      }

      @keyframes float-slow {
        0%, 100% {
          transform: translateY(0) translateX(0) scale(1);
        }
        50% {
          transform: translateY(-15px) translateX(10px) scale(1.02);
        }
      }

      @keyframes float-delayed {
        0%, 100% {
          transform: translateY(0) translateX(0);
        }
        50% {
          transform: translateY(12px) translateX(-12px);
        }
      }

      @keyframes gradient-x {
        0%, 100% {
          background-position: 0% 50%;
        }
        50% {
          background-position: 100% 50%;
        }
      }

      .animate-gradient-wave {
        background-size: 200% 200%;
        animation: gradientWave 30s ease-in-out infinite;
      }

      .animate-gradient-flow {
        background-size: 200% 200%;
        animation: gradientFlow 40s ease-in-out infinite;
      }

      .animate-breathe {
        animation: breathe 20s ease-in-out infinite;
      }

      .animate-float {
        animation: float 25s ease-in-out infinite;
      }

      .animate-float-slow {
        animation: float-slow 30s ease-in-out infinite;
      }

      .animate-float-delayed {
        animation: float-delayed 35s ease-in-out infinite;
        animation-delay: 2s;
      }

      .animate-gradient-x {
        animation: gradient-x 12s ease-in-out infinite;
        background-size: 200% 200%;
      }


    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Base gradient layer - soft and peaceful */}
        <div className="absolute inset-0" style={{ background: 'var(--background-color)' }} />
        
        {/* Primary flowing gradient - gentle wave */}
        <div 
          className="absolute inset-0 opacity-30 animate-gradient-wave"
          style={{
            background: `linear-gradient(120deg, 
              transparent 0%, 
              rgba(99, 102, 241, 0.15) 10%,
              rgba(59, 130, 246, 0.2) 20%, 
              rgba(147, 51, 234, 0.15) 30%, 
              rgba(59, 130, 246, 0.1) 40%,
              rgba(99, 102, 241, 0.2) 50%, 
              rgba(147, 51, 234, 0.15) 60%,
              rgba(59, 130, 246, 0.2) 70%,
              rgba(99, 102, 241, 0.15) 80%,
              rgba(147, 51, 234, 0.1) 90%,
              transparent 100%)`,
            backgroundSize: '200% 200%'
          }}
        />
        
        {/* Secondary flowing layer - slower, softer */}
        <div 
          className="absolute inset-0 opacity-20 animate-gradient-flow"
          style={{
            background: `linear-gradient(60deg,
              transparent 0%,
              rgba(147, 51, 234, 0.1) 15%,
              rgba(59, 130, 246, 0.15) 30%,
              rgba(99, 102, 241, 0.1) 45%,
              rgba(147, 51, 234, 0.15) 60%,
              rgba(59, 130, 246, 0.1) 75%,
              transparent 100%)`,
            backgroundSize: '200% 200%'
          }}
        />
        
        {/* Breathing gradient layer for ambience */}
        <div 
          className="absolute inset-0 animate-breathe"
          style={{
            background: `radial-gradient(circle at 50% 50%, 
              rgba(99, 102, 241, 0.1) 0%, 
              transparent 70%)`
          }}
        />
        
        {/* Ultra subtle floating orbs - like bokeh */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-float-slow" />
        <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-float-delayed" />
        <div className="absolute bottom-1/4 left-1/2 w-64 h-64 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-float" />
      </div>

      {/* Mesh gradient effect */}
      <div 
        className="absolute inset-0 opacity-40" 
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='60' height='60' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 60 0 L 0 0 0 60' fill='none' stroke='rgba(148, 163, 184, 0.05)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")`
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 text-center z-10 h-full flex flex-col justify-center pt-16 sm:pt-20">
        {/* Logo with animation */}
        <motion.div
          initial={{ opacity: 0, y: -40, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="mb-6 sm:mb-8 flex justify-center relative"
        >
          <motion.img
            src={logo}
            alt="Amaze Puzzles Logo"
            className="w-72 sm:w-80 md:w-96 lg:w-[28rem] object-contain"
            style={{
              filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
              transition: 'filter 0.3s ease'
            }}
            initial={{ rotate: 0 }}
            animate={{ rotate: [0, 8, -8, 0] }}
            transition={{ duration: 2, ease: 'easeInOut' }}
          />
        </motion.div>

        {/* Main heading */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
          style={{ color: 'var(--home-text-primary)' }}
        >
          <span className="block mb-2">Through the power of</span>
          <span style={{ color: 'var(--home-text-primary)' }}>
            puzzles & play-based learning
          </span>
          <span className="block mt-2 text-4xl md:text-5xl" style={{ color: 'var(--home-text-secondary)' }}>
            Amaze Puzzles™ helps visually impaired learners acquire, develop, and maintain essential life skills.
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed"
          style={{ color: 'var(--home-text-secondary)' }}
        >
          Enhance tactile literacy with our fun, engaging puzzles.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-6"
        >
          <button
            onClick={handleLearnMore}
            className="group relative text-white px-8 py-4 rounded-2xl text-lg font-semibold transition-all duration-300 flex items-center space-x-3 shadow-lg hover:shadow-2xl hover:scale-105 overflow-hidden"
            style={{ backgroundColor: 'var(--home-button-primary)' }}
          >
            {/* Button gradient animation on hover */}
            <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'var(--home-button-hover)' }} />
            <span className="relative">Learn More</span>
            <ArrowRight className="relative w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 rounded-full flex justify-center backdrop-blur-sm" style={{ borderColor: 'var(--home-text-muted)', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
            <motion.div 
              className="w-1 h-3 rounded-full mt-2"
              style={{ backgroundColor: 'var(--home-text-primary)' }}
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;